import { useCallback, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getEnv } from "@/config/env";

const EVIDENCE_BASE_API_URL =
  getEnv("VITE_EVIDENCE_BASE_API_URL");

interface FetchFileOptions {
  /** Ask the API to render an Office document to PDF for preview. */
  format?: "pdf";
  /**
   * Content type to stamp on the blob. The API doesn't reliably label PDFs (converted ones
   * included), and an <iframe> pointed at an untyped blob downloads it instead of rendering.
   */
  mimeType?: string;
}

const fileUrl = (documentId: string, format?: "pdf") => {
  const query = format ? `?format=${format}` : "";
  return `${EVIDENCE_BASE_API_URL}/api/evidence/documents/${documentId}/file${query}`;
};

export function useDocumentFile() {
  const { getAccessToken } = useAuth();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Kept separately so callers can distinguish a failed PDF conversion (422) from a transport
  // or auth failure, and fall back rather than offering a pointless retry.
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  const fetchFile = useCallback(
    async (documentId: string, options: FetchFileOptions = {}) => {
      setIsLoading(true);
      setError(null);
      setErrorStatus(null);
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });

      try {
        const token = await getAccessToken();
        if (!token) throw new Error("Not authenticated");

        const response = await fetch(fileUrl(documentId, options.format), {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          setErrorStatus(response.status);
          throw new Error(`Failed to load file: ${response.status}`);
        }

        const blob = await response.blob();
        const typed = options.mimeType ? new Blob([blob], { type: options.mimeType }) : blob;
        setBlobUrl(URL.createObjectURL(typed));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load file");
      } finally {
        setIsLoading(false);
      }
    },
    [getAccessToken]
  );

  /**
   * Save the original stored file under its original name. The previewed blob may be a PDF
   * rendering of an Office document, which must not be saved with a .docx/.pptx name.
   */
  const downloadOriginal = useCallback(
    async (documentId: string, filename: string) => {
      const token = await getAccessToken();
      if (!token) throw new Error("Not authenticated");
      const response = await fetch(fileUrl(documentId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Failed to download file: ${response.status}`);

      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Defer so the browser has started the download before the URL goes away.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    },
    [getAccessToken]
  );

  const clearFile = useCallback(() => {
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setError(null);
    setErrorStatus(null);
  }, []);

  return { blobUrl, isLoading, error, errorStatus, fetchFile, downloadOriginal, clearFile };
}
